import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import { ApiErrorResult_Constants } from '@siza-staff-care/shared/data';
import { AuthStore, JadeService } from '@siza-staff-care/shared/global-services';
import { EMPTY, Observable, Subject, TimeoutError, of, throwError } from 'rxjs';
import { catchError, delay, map, retry, switchMap, tap } from 'rxjs/operators';
import { NetworkStatsStore } from './network-stats.store';

export const MAX_RETRIES = new InjectionToken<number>('maxRetries');

let requestNum = 1;

@Injectable()
export class Interceptor implements HttpInterceptor {
	private refreshSubject!: Subject<unknown>;

	constructor(
		@Inject(MAX_RETRIES) private maxRetries: number,
		private snackBar: MatSnackBar,
		private appState: AppStateService,
		private statsStore: NetworkStatsStore,
		private auth: AuthStore,
		private jade: JadeService,
	) {
		this.createNewRefreshSubject();
	}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		let modifiedRequest = req;

		if (req.method === 'POST' && typeof req.body === 'string' && /^\{/.test(req.body)) {
			modifiedRequest = modifiedRequest.clone({
				headers: req.headers.set('Content-Type', 'application/json'),
			});
		}

		const num = requestNum++;
		const startTime = new Date();

		modifiedRequest = modifiedRequest.clone({
			headers: req.headers
				.set('X-SC-Client', 'ng-new')
				.set('X-SC-Time', startTime.toISOString())
				.set('X-SC-Request-Num', String(num)),
		});

		this.statsStore.logRequest({
			request: modifiedRequest,
			startTime,
			number: num,
		});

		const isRefresh = !!req.url.match('refresh');
		if (this.auth.loggingOut || isRefresh) {
			return next.handle(modifiedRequest);
		}

		return next.handle(modifiedRequest).pipe(
			tap((response) => {
				if (response instanceof HttpResponse) {
					const requestTime = modifiedRequest.headers.get('X-SC-Time');
					const requestNum = Number(modifiedRequest.headers.get('X-SC-Request-Num'));
					const diff = Date.now() - new Date(requestTime ?? '').getTime();

					this.statsStore.logResponse({
						response,
						timeTakenMs: diff,
						number: requestNum,
					});
				}
			}),
			retry({
				count: this.maxRetries,
				delay: (error, retryCount) => {
					if (error instanceof TimeoutError) {
						this.statsStore.bumpRetries();

						if (isRefresh) {
							// Just retry
							return of(true);
						}
						this.snackBar.open(`The request timed out - retrying (${retryCount}/${this.maxRetries})`, '', {
							duration: 3000,
						});

						return of(true);
					}

					return throwError(() => error);
				},
			}),
			catchError((error) => {
				if (error instanceof TimeoutError) {
					this.statsStore.bumpRetriesFailed();

					this.snackBar.open('The request timed out - retries failed', 'Close', {
						duration: 1_000_000_000,
					});

					return EMPTY;
				}

				if (error instanceof HttpErrorResponse) {
					if (this.checkTokenExpiryErr(error)) {
						return this.ifTokenExpired().pipe(
							retry({
								delay: (err) => {
									// Up to 3 retries to refresh the token
									let refreshRetries = 1;
									return of(err).pipe(
										map((tokenRefreshErr) => {
											if (
												tokenRefreshErr instanceof HttpErrorResponse &&
												tokenRefreshErr.error?.errorType
											) {
												// We got back an ApiErrorResult - abort immediately.
												throw tokenRefreshErr;
											}

											if (refreshRetries++ === 3) {
												throw tokenRefreshErr;
											}

											return tokenRefreshErr;
										}),
										delay(500),
									);
								},
							}),
							catchError((err) => {
								if (err instanceof HttpErrorResponse && err.error?.errorType) {
									const errorType = err.error.errorType;
									let msg = '';
									switch (errorType) {
										case ApiErrorResult_Constants.ErrorType_SessionEnded:
											msg = 'Session ended';
											break;
										case ApiErrorResult_Constants.ErrorType_SessionRemoved:
											msg = 'Session removed';
											break;
									}

									this.auth.logOut({ forced: true, message: msg });
								} else {
									this.auth.forceLogin();
								}
								return EMPTY;
							}),
							switchMap(() => {
								return next.handle(this.updateHeader(modifiedRequest));
							}),
						);
					} else {
						// Token not expired - just a normal HTTP error
						switch (error.status) {
							case 401:
								// Special case for 401 when logging in - probably a password error, etc.
								if (this.auth.loggingIn) {
									return throwError(() => error);
								}
								// 401 but already checked for token expiry - must not be authenticated - ignore.
								return EMPTY;

							default:
								break;
						}
					}
				}

				throw error;
			}),
		);
	}

	private checkTokenExpiryErr(error: HttpErrorResponse): boolean {
		const isExpiry = !!(this.appState.isAuthenticated && error.status && error.status === 401);
		return isExpiry;
	}

	private ifTokenExpired() {
		// If the token is expired, pauses requests and uses the refresh_token to get a new access_token
		// whereafter it then replays the requests using the new access_token.
		//
		// The access_token is short-lived, but the refresh_token is long-lived and allows getting a new
		// access_token after it expires.
		//
		// See: https://stackoverflow.com/questions/45202208/angular-4-interceptor-retry-requests-after-token-refresh
		// and: https://medium.com/@this.is.samy/angular-auto-session-recovery-interceptor-42e8233cfa23

		const first = !this.refreshSubject.observed;
		this.refreshSubject.subscribe({
			complete: () => {
				this.createNewRefreshSubject();
			},
			error: () => {
				this.createNewRefreshSubject();
			},
		});

		if (first) {
			this.refreshToken().subscribe(this.refreshSubject);
		}
		return this.refreshSubject;
	}

	private refreshToken() {
		if (!this.appState.isAuthenticated) {
			// isAuthenticated is false, so bailing
			return of(false);
		}

		return this.jade.postRefresh().pipe(
			map((result) => {
				this.appState.updateJwt(result);
				return true;
			}),
		);
	}

	private updateHeader(req: HttpRequest<unknown>) {
		const authToken: string = this.appState.jwt ?? '';
		req = req.clone({
			headers: req.headers.set('Authorization', `Bearer ${authToken}`),
		});
		return req;
	}

	private createNewRefreshSubject() {
		this.refreshSubject = new Subject<unknown>();
	}
}
